import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import Portlet from '@ui/portlet/Portlet';
import FormButton from '@ui/button/FormButton';
import Message from '@ui/message/Message';
import Spinner from '@ui/spinner/Spinner';
import Tabs from '@ui/tabs/Tabs';
import { MonthGridMonth } from '@ui/calendar/MonthGrid';
import MarketInsight from '@app/model/market_insight/MarketInsight';
import { MarketPaceDay } from '@app/model/market_insight/MarketPace';
import { useMarketPaceQuery } from '@app/model/market_insight/service/MarketPaceServiceHooks';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { buildDemandChartOptions } from './market_pace/MarketDemandChartOptions';
import MarketDemandCalendar from './market_pace/MarketDemandCalendar';
import MarketDemandLegend from './market_pace/MarketDemandLegend';
import { PACE_ERROR_LABEL, runLabel } from './market_pace/MarketPaceText';
import { MarketDemandView, useIsPhone, useMarketDemandView } from './market_pace/useMarketDemandView';
// The Market Demand panel of Market Pulse: the demand of the market per stay date, with the pace of
// the hotel against its market (grill of 2026-09-29). Two views (decision 18): the Chart (columns
// and the pace strip, direction A) and the Calendar (direction C). The page loads 61 stay dates per
// page (skipDays); the next page starts after the last date of this page, so no date repeats (R4).
const PAGE_DAYS = 61;
const PHONE_CHART_DAYS = 14;
const VIEW_OPTIONS: { label: string, value: MarketDemandView }[] = [{ label: 'Chart', value: 'chart' }, { label: 'Calendar', value: 'calendar' }];
const MarketInsightMarketAreaStatesGraph: React.FunctionComponent<{
	marketInsight: MarketInsight,
	stateIndex: number,
	onSelectStateIndex: (index: number) => void,
	onSetSkipDays: (value: number) => void,
	skipDays: number
}> = ({ marketInsight, stateIndex, onSelectStateIndex, onSetSkipDays, skipDays }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const [view, setView] = useMarketDemandView();
	const phone = useIsPhone();
	const states = marketInsight.marketRegionStates ?? [];
	const total = marketInsight.totalMarketRegionStates;
	const paceEnabled = companyHotel.settings.main.enableMarketPace === true;
	const paceQuery = useMarketPaceQuery(companyHotel.id, states[0]?.statDate, states[states.length - 1]?.statDate, paceEnabled);
	const pace = useMemo((): Map<string, MarketPaceDay> | null => {
		if (!paceEnabled || paceQuery.data === undefined || paceQuery.data === null) return null;
		return new Map(paceQuery.data.days.map(day => [day.statDate, day]));
	}, [paceEnabled, paceQuery.data]);
	if (states.length === 0) {
		return <Portlet title="Market Demand Chart">
			<Message state="warning">No market data available for this period.</Message>
		</Portlet>;
	}
	// The window of the view: the chart shows the whole page (61 dates) or 14 dates on a phone; the
	// calendar shows the months of the page, or the month of the selected date on a phone.
	const selected = Math.min(Math.max(stateIndex, 0), states.length - 1);
	const chartSize = phone ? PHONE_CHART_DAYS : PAGE_DAYS;
	const chartStart = Math.floor(selected / chartSize) * chartSize;
	const chartStates = states.slice(chartStart, chartStart + chartSize);
	const pageMonths: MonthGridMonth[] = [];
	states.forEach(state => {
		const date = moment(state.statDate);
		const last = pageMonths[pageMonths.length - 1];
		if (last === undefined || last.year !== date.year() || last.month !== date.month()) pageMonths.push({ year: date.year(), month: date.month() });
	});
	const selectedMonth = moment(states[selected].statDate);
	const calendarMonths = phone ? pageMonths.filter(m => m.year === selectedMonth.year() && m.month === selectedMonth.month()) : pageMonths;
	const monthPosition = pageMonths.findIndex(m => m.year === selectedMonth.year() && m.month === selectedMonth.month());
	// The arrows: inside the page first (a phone group or a month), then the previous or next page.
	const firstOfMonth = (position: number) => states.findIndex(state => moment(state.statDate).year() === pageMonths[position].year
		&& moment(state.statDate).month() === pageMonths[position].month);
	const monthBackInside = phone && monthPosition > 0;
	const canBack = skipDays > 0 || (view === 'chart' ? chartStart > 0 : monthBackInside);
	const canNext = skipDays + PAGE_DAYS < total || (view === 'chart'
		? chartStart + chartSize < states.length
		: phone && monthPosition < pageMonths.length - 1);
	const back = () => {
		if (view === 'chart' && chartStart > 0) onSelectStateIndex(chartStart - chartSize);
		else if (view === 'calendar' && monthBackInside) onSelectStateIndex(firstOfMonth(monthPosition - 1));
		else if (skipDays > 0) {
			onSetSkipDays(Math.max(0, skipDays - PAGE_DAYS));
			onSelectStateIndex(PAGE_DAYS - 1);
		}
	};
	const next = () => {
		if (view === 'chart' && chartStart + chartSize < states.length) onSelectStateIndex(chartStart + chartSize);
		else if (view === 'calendar' && phone && monthPosition < pageMonths.length - 1) onSelectStateIndex(firstOfMonth(monthPosition + 1));
		else if (skipDays + PAGE_DAYS < total) {
			onSetSkipDays(skipDays + PAGE_DAYS);
			onSelectStateIndex(0);
		}
	};
	const rangeLabel = view === 'chart'
		? `${moment(chartStates[0].statDate).format('D MMM')} – ${moment(chartStates[chartStates.length - 1].statDate).format('D MMMM YYYY')}`
		: calendarMonths.map(m => moment({ year: m.year, month: m.month, day: 1 }).format('MMM')).join(' – ') + ` ${calendarMonths[calendarMonths.length - 1].year}`;
	const note = !paceEnabled
		? undefined
		: paceQuery.isLoading
			? <span className="inline-flex items-center gap-x-1.5"><Spinner />Pace</span>
			: paceQuery.isError
				? PACE_ERROR_LABEL
				: <span className="inline-flex items-center">{runLabel(paceQuery.data?.run ?? null)}</span>;
	return <Portlet title={view === 'chart' ? 'Market Demand Chart' : 'Market Demand Calendar'} note={note}>
		<div className="-mt-1 mb-4 flex items-center gap-x-1">
			<div className="grow"><Tabs options={VIEW_OPTIONS} value={view} onChange={setView} /></div>
		</div>
		{view === 'chart'
			? <div className="mb-2">
				<HighchartsReact highcharts={Highcharts}
					options={buildDemandChartOptions({
						states: chartStates, pace, selectedIndex: selected - chartStart, phone,
						onSelect: index => onSelectStateIndex(chartStart + index),
					})} />
			</div>
			: <div className="mb-4">
				<MarketDemandCalendar states={states} pace={pace} months={calendarMonths} selectedIndex={selected} onSelect={onSelectStateIndex} />
			</div>}
		<div className="flex flex-wrap items-start gap-x-1">
			<MarketDemandLegend view={view} showPace={pace !== null} source={paceQuery.data?.ownSource ?? 'BOOKING'} />
		</div>
		<div className="mt-4 flex justify-center items-center gap-x-4">
			<FormButton disabled={!canBack} onClick={back} className="btn btn-white h-8 px-2">
				<ChevronLeftIcon className="w-5 h-5" />
			</FormButton>
			<p className="text-sm text-gray-600">{rangeLabel}</p>
			<FormButton disabled={!canNext} onClick={next} className="btn btn-white h-8 px-2">
				<ChevronRightIcon className="w-5 h-5" />
			</FormButton>
		</div>
	</Portlet>;
};
export default MarketInsightMarketAreaStatesGraph;
